import { focusHaloFlush, Input, panelMotion, press } from "@pax/ui";
import { Search, X } from "lucide-react";
import type { ReactNode } from "react";

/** The one thing a bar offers to make, filled so it reads as the way forward. */
export const barAction = `relative flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-full bg-foreground px-3.5 text-sm font-medium text-background hover:bg-foreground/85 max-sm:w-9 max-sm:px-0 ${panelMotion} ${press} ${focusHaloFlush}`;

/**
 * The bar a list page opens with. It stays at the top while the list scrolls under it, and holds,
 * in order: the way back on a page below one of the ways, the title or the search field, filters,
 * and the page's actions.
 */
export function ScreenBar({ children }: { children: ReactNode }) {
  return (
    <div className="sticky top-0 z-20 bg-screen/70 py-3 backdrop-blur-xl">
      <div className="app-reading-width flex items-center gap-2.5 sm:gap-3">{children}</div>
    </div>
  );
}

/** A bar's search, as a field rather than a link: this is where the search happens. */
export function SearchField({
  value,
  onChange,
  placeholder,
  label,
  autoFocus,
  maxLength = 200,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  label: string;
  autoFocus?: boolean;
  maxLength?: number;
}) {
  return (
    <div className="relative min-w-0 flex-1">
      <Search
        aria-hidden
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
      />
      <Input
        type="search"
        value={value}
        maxLength={maxLength}
        autoFocus={autoFocus}
        onChange={event => onChange(event.currentTarget.value)}
        placeholder={placeholder}
        aria-label={label}
        className="h-9 rounded-full border-0 bg-foreground/[0.07] pr-9 pl-9"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear search"
          className={`absolute top-1/2 right-1.5 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground hover:bg-foreground/10 hover:text-foreground ${panelMotion} ${press} ${focusHaloFlush}`}
        >
          <X aria-hidden className="size-3.5" />
        </button>
      )}
    </div>
  );
}
